import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, AlertTriangle, Send, Loader2, Building2, Car, User, Shield, ChevronDown, ChevronUp, PlusCircle } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;

// 🆕 Feb 2026 — ErrorBoundary dedicato: garantisce che la pagina non resti mai bianca
// anche se un render del sotto-albero React fallisce (es. su specifici browser/estensioni).
class PartnerOfferErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, msg: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, msg: String(error?.message || error || "Errore sconosciuto") };
  }
  componentDidCatch(error, info) {
    try { console.error("[PartnerOfferSubmit] render error:", error, info); } catch { /* no-op */ }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 p-4" data-testid="partner-offer-fallback">
          <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center border-2 border-emerald-300">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Operazione completata</h1>
            <p className="text-sm text-slate-600">
              Si è verificato un problema di visualizzazione. Se hai cliccato Invia, l'offerta è stata ricevuta.
            </p>
            <p className="text-[11px] text-slate-400 mt-4 break-words">Dettaglio tecnico: {this.state.msg}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// 🆕 Feb 2026 — Form pubblico per la sottomissione offerta partner
// Route: /inserisci-offerta/:token
// Il token è univoco per (preventivo, partner) e scade dopo 30 giorni.
function PartnerOfferSubmitInner() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [form, setForm] = useState({
    company: "",
    coverages: [{ name: "RCA", premium: "", deductible: "", sum_insured: "" }],
    commissions: "",
    frazionamento: "annuale",
    installments_count: "1",
    installment_amount: "",
    max_coverage: "",
    deductibles: "",
    valid_until: "",
    notes: "",
  });
  const [coverageSuggestions, setCoverageSuggestions] = useState([]); // 🆕 autocomplete da CRM
  const [companySuggestions, setCompanySuggestions] = useState([]); // 🆕 Feb 2026 — autocomplete Compagnie
  // 🆕 Feb 2026 — Collassabilità sezione Cliente (default: aperta)
  const [clientOpen, setClientOpen] = useState(true);
  // 🆕 Feb 2026 — Collassabilità sezioni Veicolo e Richiesta preventivo (default: aperte)
  const [vehicleOpen, setVehicleOpen] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(true);
  const firstPremiumRef = useRef(null);
  const commissionsRef = useRef(null);

  const totalPremium = form.coverages.reduce((s, c) => s + (parseFloat(c.premium) || 0), 0);
  // 🆕 Feb 2026 — Totale offerta = somma premi garanzie + commissioni
  const commissionsNum = parseFloat(form.commissions) || 0;
  const totalOffer = totalPremium + commissionsNum;
  // 🆕 Feb 2026 — Rileva duplicati nel campo nome garanzia (case-insensitive)
  const duplicateFlags = form.coverages.map((c, i) => {
    const n = (c.name || "").trim().toLowerCase();
    if (!n) return null;
    const firstIdx = form.coverages.findIndex((x) => (x.name || "").trim().toLowerCase() === n);
    return firstIdx !== -1 && firstIdx < i ? c.name.trim() : null;
  });
  const addCoverage = () => setForm((f) => ({ ...f, coverages: [...f.coverages, { name: "", premium: "", deductible: "", sum_insured: "" }] }));
  const removeCoverage = (i) => setForm((f) => ({ ...f, coverages: f.coverages.filter((_, idx) => idx !== i) }));
  const updateCoverage = (i, key, val) => setForm((f) => ({ ...f, coverages: f.coverages.map((c, idx) => idx === i ? { ...c, [key]: val } : c) }));
  const [submitting, setSubmitting] = useState(false);
  // 🆕 Feb 2026 — Lock sincrono anti-doppio-submit (setState async non blocca doppio-click)
  const submitLockRef = useRef(false);
  const [submitError, setSubmitError] = useState(null);
  // 🆕 Feb 2026 — Multi-submission con MODALE centrata (no Radix: fixed overlay semplice).
  // `showSuccessModal` controlla la visibilità della modale post-invio.
  // `activeVariant` è l'etichetta della variante corrente ("Semestrale (2 rate)") mostrata
  // in modo prominente in alto al form quando si sta lavorando a una duplicazione.
  const [recentSubmission, setRecentSubmission] = useState(null);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [lastSubmittedForm, setLastSubmittedForm] = useState(null);
  const [dupFraz, setDupFraz] = useState("semestrale");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [activeVariant, setActiveVariant] = useState(null); // { label: "Semestrale", rate: "2 rate" } | null

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/partner-offer/${token}`);
        setState({ loading: false, error: null, data: r.data });
        // Pre-compila company se il partner è una compagnia
        if (r.data?.partner?.name && r.data?.partner_type === "company") {
          setForm((f) => ({ ...f, company: r.data.partner.name }));
        }
        // 🆕 Feb 2026 — Fetch autocomplete garanzie dalle Liste Preventivo del CRM
        try {
          const s = await axios.get(`${API}/public/partner-offer/${token}/coverage-suggestions`);
          setCoverageSuggestions(Array.isArray(s.data?.items) ? s.data.items : []);
        } catch { /* opzionale */ }
        // 🆕 Feb 2026 — Fetch autocomplete compagnie dal CRM
        try {
          const sc = await axios.get(`${API}/public/partner-offer/${token}/companies-suggestions`);
          setCompanySuggestions(Array.isArray(sc.data?.items) ? sc.data.items : []);
        } catch { /* opzionale */ }
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "Link non valido o scaduto.", data: null });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    const validCoverages = form.coverages.filter((c) => (c.name || "").trim() && parseFloat(c.premium) > 0);
    if (validCoverages.length === 0) {
      setSubmitError("Inserisci almeno una garanzia (nome + premio > 0).");
      return;
    }
    // 🆕 Blocco submit se ci sono duplicati
    const dupNames = duplicateFlags.filter(Boolean);
    if (dupNames.length > 0) {
      setSubmitError(`Rimuovi i duplicati prima di inviare: ${[...new Set(dupNames)].join(", ")}`);
      return;
    }
    setSubmitting(true);
    // 🆕 Feb 2026 — Guard sincrono anti-doppio-invio: setState è asincrono quindi un doppio-click
    // rapido (o un doppio evento click+touchend su mobile) può bypassare `disabled={submitting}`.
    if (submitLockRef.current) {
      setSubmitting(false);
      return;
    }
    submitLockRef.current = true;
    try {
      await axios.post(`${API}/public/partner-offer/${token}`, {
        company: form.company,
        coverages: validCoverages.map((c) => ({
          name: c.name.trim(),
          premium: parseFloat(c.premium),
          deductible: c.deductible || null,
          sum_insured: c.sum_insured ? parseFloat(c.sum_insured) : null,
        })),
        commissions: form.commissions ? parseFloat(form.commissions) : null,
        frazionamento: form.frazionamento,
        installments_count: form.installments_count ? parseInt(form.installments_count, 10) : null,
        installment_amount: form.installment_amount ? parseFloat(form.installment_amount) : null,
        max_coverage: form.max_coverage ? parseFloat(form.max_coverage) : null,
        deductibles: form.deductibles || null,
        valid_until: form.valid_until || null,
        notes: form.notes || null,
      });
      // 🆕 Feb 2026 — Multi-submission INLINE (no Dialog, no portal, no DOM manipulation).
      // Il partner può continuare a inviare offerte con frazionamenti diversi:
      //  • salvo lo snapshot in `recentSubmission` per il banner "Offerta inviata!";
      //  • azzero premi, commissioni e rata (campi che DEVONO cambiare tra una compagnia e l'altra);
      //  • il resto del form (compagnia, garanzie, frazionamento) resta compilato così il partner
      //    può semplicemente cambiare frazionamento dal <Select> e re-inviare;
      //  • il redirect finale a /offerta-inviata avviene SOLO quando il partner clicca "Ho finito".
      setRecentSubmission({
        company: form.company,
        frazionamento: form.frazionamento,
        count: submissionsCount + 1,
      });
      // Snapshot pieno del form inviato (serve al pulsante "Duplica")
      setLastSubmittedForm({ ...form, coverages: (form.coverages || []).map((c) => ({ ...c })) });
      setSubmissionsCount((n) => n + 1);
      setForm((f) => ({
        ...f,
        coverages: (f.coverages || []).map((c) => ({ ...c, premium: "" })),
        commissions: "",
        installment_amount: "",
      }));
      // 🆕 Feb 2026 — Apre la MODALE centrata di successo (sostituisce il banner inline)
      setShowSuccessModal(true);
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || "Errore invio offerta. Riprova.");
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  // 🆕 Feb 2026 — Il partner conclude la sessione multi-invio e viene mandato alla pagina di conferma.
  const finishAndRedirect = () => {
    const params = new URLSearchParams();
    if (token) params.set("token", token);
    if (recentSubmission?.company) params.set("company", recentSubmission.company);
    if (recentSubmission?.frazionamento) params.set("frazionamento", recentSubmission.frazionamento);
    if (submissionsCount) params.set("count", String(submissionsCount));
    window.location.href = `/offerta-inviata?${params.toString()}`;
  };

  // 🆕 Feb 2026 — Mappa frazionamento → numero rate (riusata per duplicazione)
  const frazToRate = { annuale: "1", semestrale: "2", quadrimestrale: "3", trimestrale: "4", mensile: "12" };
  // 🆕 Feb 2026 — Label leggibile per il frazionamento (mostrato in alto al form sulla copia)
  const frazLabels = {
    annuale: { label: "Annuale", rate: "1 rata" },
    semestrale: { label: "Semestrale", rate: "2 rate" },
    quadrimestrale: { label: "Quadrimestrale", rate: "3 rate" },
    trimestrale: { label: "Trimestrale", rate: "4 rate" },
    mensile: { label: "Mensile", rate: "12 rate" },
  };

  // 🆕 Feb 2026 — Duplica l'ultima offerta inviata: mantiene TUTTI i dati (compagnia, garanzie,
  // massimali, franchigie, note…) e cambia solo il frazionamento scelto; azzera gli importi
  // (premi, commissioni, rata) così il partner inserisce quelli nuovi.
  // Nessuna manipolazione DOM: solo setState → React smonta/rimonta i figli in sicurezza.
  const duplicateLastOffer = () => {
    if (!lastSubmittedForm) return;
    const nextCov = (lastSubmittedForm.coverages || []).map((c) => ({ ...c, premium: "" }));
    setForm({
      ...lastSubmittedForm,
      coverages: nextCov.length ? nextCov : [{ name: "RCA", premium: "", deductible: "", sum_insured: "" }],
      commissions: "",
      frazionamento: dupFraz,
      installments_count: frazToRate[dupFraz] || "1",
      installment_amount: "",
    });
    // Imposta l'etichetta della variante corrente (mostrata in modo prominente in alto al form)
    setActiveVariant(frazLabels[dupFraz] || { label: dupFraz, rate: "" });
    setShowSuccessModal(false);
    setSubmitError(null);
    setTimeout(() => {
      try {
        firstPremiumRef.current?.focus?.();
        firstPremiumRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      } catch { /* no-op */ }
    }, 120);
  };

  // 🆕 Feb 2026 — Nuova offerta da zero: azzera compagnia + garanzie + importi, mantiene
  // il contesto preventivo (frazionamento default, condizioni base precedenti).
  const startNewOffer = () => {
    const base = lastSubmittedForm || form;
    setForm({
      company: partner.name || "",
      coverages: [{ name: "RCA", premium: "", deductible: "", sum_insured: "" }],
      commissions: "",
      frazionamento: base.frazionamento || "annuale",
      installments_count: base.installments_count || "1",
      installment_amount: "",
      max_coverage: base.max_coverage || "",
      deductibles: base.deductibles || "",
      valid_until: base.valid_until || "",
      notes: "",
    });
    setActiveVariant(null); // reset etichetta variante
    setShowSuccessModal(false);
    setSubmitError(null);
    setTimeout(() => {
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* no-op */ }
    }, 60);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-600" data-testid="partner-offer-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Caricamento…
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center border border-rose-200" data-testid="partner-offer-error">
          <AlertTriangle className="h-14 w-14 text-rose-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Link non valido</h1>
          <p className="text-sm text-slate-600">{state.error}</p>
          <p className="text-xs text-slate-500 mt-4">Contatta l'agenzia per ricevere un nuovo link.</p>
        </div>
      </div>
    );
  }

  const q = state.data?.quote || {};
  const c = state.data?.client || {};
  const ag = state.data?.agency || {};
  const partner = state.data?.partner || {};
  const expiresAt = (state.data?.expires_at || "").substring(0, 10);

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-3">
      <div className="max-w-4xl mx-auto">
        {/* Header — 🆕 Feb 2026: intestazione reale dell'agenzia (nome, indirizzo, logo) */}
        <div className="bg-white rounded-t-lg border border-slate-200 border-b-0 p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {(() => {
              const raw = ag.logo_url || "";
              const src = raw.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${raw}` : raw;
              return src ? <img src={src} alt="logo agenzia" className="h-12 w-12 rounded-md object-contain border border-slate-200 bg-white" /> : null;
            })()}
            <div>
              <div className="text-lg font-bold text-slate-800" data-testid="partner-offer-agency-name">{ag.name || ag.legal_name || "Agenzia"}</div>
              {(() => {
                const parts = [
                  [ag.address, ag.house_number].filter(Boolean).join(" ").trim(),
                  ag.cap,
                  ag.city,
                  ag.province ? `(${ag.province})` : null,
                ].filter(Boolean);
                return parts.length ? <div className="text-xs text-slate-500" data-testid="partner-offer-agency-address">{parts.join(", ")}</div> : null;
              })()}
              <div className="text-xs text-slate-500 mt-0.5">Richiesta offerta per {partner.name || "partner"}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Preventivo</div>
            <div className="font-mono text-sm font-bold text-indigo-700">{q.quote_number || "—"}</div>
            {expiresAt && <div className="text-[10px] text-slate-500 mt-0.5">Link valido fino al {expiresAt.split("-").reverse().join("/")}</div>}
          </div>
        </div>

        {/* Recap pre-compilato */}
        <div className="bg-white border border-slate-200 border-b-0 border-t-0 p-5 space-y-4" data-testid="partner-offer-recap">
          {/* Cliente — 🆕 Feb 2026: sezione collassabile (default aperta) */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[11px] uppercase tracking-wider text-blue-700 font-bold flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Cliente</h2>
              <button
                type="button"
                onClick={() => setClientOpen((v) => !v)}
                data-testid="partner-offer-client-toggle"
                aria-expanded={clientOpen}
                aria-label={clientOpen ? "Nascondi dati cliente" : "Mostra dati cliente"}
                title={clientOpen ? "Nascondi dati cliente" : "Mostra dati cliente"}
                className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 px-2 py-1 text-[11px] font-semibold transition-colors"
              >
                {clientOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                <span>{clientOpen ? "Nascondi" : "Mostra"}</span>
              </button>
            </div>
            {clientOpen && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm bg-blue-50/40 rounded-md p-3 border border-blue-100" data-testid="partner-offer-client-body">
                <Field k="Nome" v={c.name} />
                <Field k="Cognome" v={c.surname} />
                <Field k="Codice fiscale" v={c.fiscal_code} />
                <Field k="Data di nascita" v={fmtDate(c.birth_date)} />
                <Field k="Luogo di nascita" v={c.birth_place} />
                <Field k="Città" v={c.city} />
                <Field k="Provincia" v={c.province} />
                <Field k="Professione" v={c.profession} />
              </div>
            )}
          </section>
          {/* Veicolo (se auto/moto) — 🆕 Feb 2026: sezione collassabile */}
          {(q.type === "auto" || q.type === "moto") && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-[11px] uppercase tracking-wider text-green-700 font-bold flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> Veicolo</h2>
                <button
                  type="button"
                  onClick={() => setVehicleOpen((v) => !v)}
                  data-testid="partner-offer-vehicle-toggle"
                  aria-expanded={vehicleOpen}
                  aria-label={vehicleOpen ? "Nascondi dati veicolo" : "Mostra dati veicolo"}
                  title={vehicleOpen ? "Nascondi dati veicolo" : "Mostra dati veicolo"}
                  className="inline-flex items-center gap-1 rounded-md border border-green-300 bg-green-50 hover:bg-green-100 text-green-800 px-2 py-1 text-[11px] font-semibold transition-colors"
                >
                  {vehicleOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  <span>{vehicleOpen ? "Nascondi" : "Mostra"}</span>
                </button>
              </div>
              {vehicleOpen && (
                <div data-testid="partner-offer-vehicle-body">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm bg-green-50/40 rounded-md p-3 border border-green-100">
                    <Field k="Targa" v={q.vehicle_plate} />
                    <Field k="Marca" v={q.vehicle_brand} />
                    <Field k="Modello" v={q.vehicle_model} />
                    <Field k="Anno immatricolazione" v={q.vehicle_year} />
                    <Field k="Alimentazione" v={q.vehicle_fuel} />
                    <Field k="Potenza (kW)" v={q.vehicle_kw} />
                    <Field k="Cilindrata (cc)" v={q.vehicle_displacement} />
                    <Field k="Classe di merito" v={q.merit_class} />
                    <Field k="Situazione assicurativa" v={q.insurance_status === "new_vehicle" ? "Nuovo veicolo da assicurare" : (q.insurance_status === "already_insured" ? "Già assicurato" : q.insurance_status)} />
                  </div>
                  {/* Bersani / RC Familiare */}
                  {q.insurance_status === "new_vehicle" && q.bersani_ref_plate && (
                    <div className="mt-3">
                      <h3 className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1.5">🎯 Bersani / RC Familiare</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm bg-emerald-50/40 rounded-md p-3 border border-emerald-100">
                        <Field k="Tipo agevolazione" v={q.bersani_type === "legge_bersani" ? "Legge Bersani" : (q.bersani_type === "rc_familiare" ? "RC Familiare" : q.bersani_type)} />
                        <Field k="Targa di riferimento" v={q.bersani_ref_plate} />
                        <Field k="Compagnia di riferimento" v={q.bersani_ref_company} />
                        <Field k="Classe merito riferimento" v={q.bersani_ref_merit_class} />
                        <Field k="Intestatario" v={q.bersani_ref_owner} />
                        <Field k="CF intestatario" v={q.bersani_ref_owner_fiscal_code} />
                        <Field k="Nascita intestatario" v={fmtDate(q.bersani_ref_owner_birth_date)} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
          {/* Richiesta preventivo — 🆕 Feb 2026: sezione collassabile */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[11px] uppercase tracking-wider text-violet-700 font-bold flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Dati preventivo</h2>
              <button
                type="button"
                onClick={() => setQuoteOpen((v) => !v)}
                data-testid="partner-offer-quote-toggle"
                aria-expanded={quoteOpen}
                aria-label={quoteOpen ? "Nascondi dati preventivo" : "Mostra dati preventivo"}
                title={quoteOpen ? "Nascondi dati preventivo" : "Mostra dati preventivo"}
                className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800 px-2 py-1 text-[11px] font-semibold transition-colors"
              >
                {quoteOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                <span>{quoteOpen ? "Nascondi" : "Mostra"}</span>
              </button>
            </div>
            {quoteOpen && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm bg-violet-50/40 rounded-md p-3 border border-violet-100" data-testid="partner-offer-quote-body">
                <Field k="Tipo polizza" v={q.type} />
                <Field k="Data decorrenza" v={fmtDate(q.start_date)} />
                <Field k="Frazionamento richiesto" v={q.frazionamento} />
                {q.coverage_request && <div className="col-span-full"><Field k="Note richiesta" v={q.coverage_request} /></div>}
              </div>
            )}
          </section>
        </div>

        {/* 🆕 Feb 2026 — Header prominente della variante quando si sta lavorando su una Duplica.
            Posizionato sopra il form, con testo grande/emphasis per indicare chiaramente la variante. */}
        {activeVariant && (
          <div
            data-testid="partner-offer-variant-header"
            className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white border-x border-slate-200 px-6 py-5 rounded-none shadow-sm"
          >
            <div className="text-[11px] uppercase tracking-widest text-indigo-100 font-semibold">Stai lavorando a questa variante</div>
            <div className="flex items-end gap-3 mt-1 flex-wrap">
              <div className="text-3xl sm:text-4xl font-black leading-tight" data-testid="partner-offer-variant-title">
                Offerta — {activeVariant.label}
              </div>
              {activeVariant.rate && (
                <div className="text-base sm:text-lg font-semibold text-indigo-100 pb-1" data-testid="partner-offer-variant-rate">
                  ({activeVariant.rate})
                </div>
              )}
            </div>
            <div className="text-xs text-indigo-100 mt-1">Compila solo gli importi e invia; gli altri dati sono già pre-compilati.</div>
          </div>
        )}

        {/* Form offerta */}
        <form onSubmit={submit} className="bg-white rounded-b-lg border border-slate-200 p-5" data-testid="partner-offer-form">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2"><Building2 className="h-5 w-5 text-emerald-600" /> La tua offerta</h2>
          <p className="text-xs text-slate-500 mb-4">Compila i campi qui sotto per inviare la tua proposta all'agenzia.</p>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Compagnia</Label>
              <Input
                data-testid="po-company"
                list="po-company-suggestions"
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                placeholder="Es. Generali, Allianz…"
              />
              <datalist id="po-company-suggestions">
                {companySuggestions.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </div>
          </div>

          {/* 🆕 Feb 2026 — Editor Multi-Copertura: N garanzie (nome + premio + franchigia + massimale) */}
          <div className="mt-5" data-testid="po-coverages-editor">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-bold text-slate-800">Garanzie proposte <span className="text-red-500">*</span></Label>
            </div>
            <div className="rounded-md border-2 border-slate-200 divide-y divide-slate-200 bg-slate-50/40">
              {form.coverages.map((c, i) => (
                <div key={i} className={`p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end ${duplicateFlags[i] ? "bg-amber-50 border-l-4 border-amber-400" : ""}`} data-testid={`po-coverage-row-${i}`}>
                  <div className="md:col-span-4">
                    <Label className="text-[10px] text-slate-600">Garanzia</Label>
                    <Input data-testid={`po-cov-name-${i}`} list="po-coverage-suggestions" value={c.name} onChange={(e) => updateCoverage(i, "name", e.target.value)} placeholder="RCA, Kasko, Furto…" />
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-[10px] text-slate-600">Premio (€) *</Label>
                    <Input data-testid={`po-cov-premium-${i}`} ref={i === 0 ? firstPremiumRef : null} type="number" step="0.01" min="0" value={c.premium} onChange={(e) => updateCoverage(i, "premium", e.target.value)} onBlur={() => { if (i === 0) { try { commissionsRef.current?.focus?.(); } catch {} } }} placeholder="0,00" />
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-[10px] text-slate-600">Massimale (€)</Label>
                    <Input data-testid={`po-cov-max-${i}`} type="number" step="10000" min="0" value={c.sum_insured} onChange={(e) => updateCoverage(i, "sum_insured", e.target.value)} placeholder="opzionale" />
                  </div>
                  <div className="md:col-span-1 flex justify-end">
                    {form.coverages.length > 1 && (
                      <button type="button" onClick={() => removeCoverage(i)} data-testid={`po-cov-remove-${i}`} className="p-2 text-rose-600 hover:bg-rose-50 rounded" title="Rimuovi garanzia">
                        ✕
                      </button>
                    )}
                  </div>
                  <div className="md:col-span-12">
                    <Label className="text-[10px] text-slate-600">Franchigia / Scoperto</Label>
                    <Input data-testid={`po-cov-deductible-${i}`} value={c.deductible} onChange={(e) => updateCoverage(i, "deductible", e.target.value)} placeholder="Es. 500€ / scoperto 10%…" />
                  </div>
                  {duplicateFlags[i] && (
                    <div className="md:col-span-12 -mt-1 flex items-center gap-1 text-[11px] text-amber-800 font-semibold" data-testid={`po-cov-duplicate-${i}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Hai già inserito una garanzia <strong className="uppercase">{duplicateFlags[i]}</strong>. Modifica il nome o rimuovi questa riga.
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* 🆕 Datalist per autocomplete dei nomi garanzia (da Liste Preventivo → Coperture) */}
            <datalist id="po-coverage-suggestions">
              {coverageSuggestions.map((s) => (
                <option key={s.code || s.label} value={s.label} />
              ))}
            </datalist>
            <Button type="button" onClick={addCoverage} data-testid="po-add-coverage" variant="outline" className="mt-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50">
              + Aggiungi garanzia
            </Button>

            {/* 🆕 Feb 2026 — Riepilogo totali: Totale garanzie + Commissioni (input) + Totale offerta */}
            <div className="mt-4 rounded-lg border-2 border-emerald-300 bg-emerald-50/60 p-4" data-testid="po-totals-row">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <Label className="text-[11px] uppercase tracking-wide text-emerald-800 font-bold">Totale garanzie (€)</Label>
                  <div className="mt-1 text-xl font-bold font-mono text-emerald-900" data-testid="po-total-coverages">
                    € {totalPremium.toFixed(2).replace(".", ",")}
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wide text-emerald-800 font-bold">Commissioni (€)</Label>
                  <Input
                    data-testid="po-commissions"
                    ref={commissionsRef}
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.commissions}
                    onChange={(e) => setForm((f) => ({ ...f, commissions: e.target.value }))}
                    placeholder="Es. 60,00"
                    className="mt-1 font-mono font-bold text-emerald-900 border-emerald-300 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wide text-emerald-800 font-bold">Totale offerta (€)</Label>
                  <div className="mt-1 text-xl font-bold font-mono text-emerald-900" data-testid="po-total-offer">
                    € {totalOffer.toFixed(2).replace(".", ",")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Frazionamento</Label>
              <Select value={form.frazionamento} onValueChange={(v) => {
                const map = { annuale: "1", semestrale: "2", quadrimestrale: "3", trimestrale: "4", mensile: "12" };
                setForm((f) => ({ ...f, frazionamento: v, installments_count: map[v] || f.installments_count }));
              }}>
                <SelectTrigger data-testid="po-frazionamento"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="annuale">Annuale</SelectItem>
                  <SelectItem value="semestrale">Semestrale</SelectItem>
                  <SelectItem value="quadrimestrale">Quadrimestrale</SelectItem>
                  <SelectItem value="trimestrale">Trimestrale</SelectItem>
                  <SelectItem value="mensile">Mensile</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Numero rate</Label>
              <Input data-testid="po-installments-count" type="number" step="1" min="1" max="60" value={form.installments_count} readOnly disabled className="bg-slate-100 cursor-not-allowed text-slate-700" placeholder="Auto da frazionamento" title="Calcolato automaticamente in base al frazionamento" />
              <p className="text-xs text-slate-500 mt-1">Calcolato automaticamente dal frazionamento</p>
            </div>
            <div>
              <Label>Importo rata (€)</Label>
              <Input data-testid="po-installment" type="number" step="0.01" min="0" value={form.installment_amount} onChange={(e) => setForm((f) => ({ ...f, installment_amount: e.target.value }))} placeholder="Se frazionato" />
            </div>
            <div>
              <Label>Validità offerta (fino al)</Label>
              <Input data-testid="po-valid-until" type="date" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} />
            </div>
            <div className="sm:col-span-2">
              <Label>Note aggiuntive</Label>
              <Textarea data-testid="po-notes" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Garanzie incluse, condizioni particolari…" />
            </div>
          </div>
          {submitError && (
            <div className="mt-4 p-3 bg-rose-50 border-l-4 border-rose-500 text-rose-800 text-sm rounded" data-testid="po-error">
              ⚠️ {submitError}
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[11px] text-slate-500">Inviando confermi di essere autorizzato a proporre questa offerta a nome di {partner.name || "te"}.</p>
            <Button type="submit" disabled={submitting} data-testid="po-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-6 text-base shadow-lg">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio…</> : <><Send className="h-4 w-4 mr-2" /> {submissionsCount > 0 ? "Invia un'altra offerta" : "Invia offerta"}</>}
            </Button>
          </div>
        </form>

        {/* 🆕 Feb 2026 — Modale di successo centrata (fixed overlay, nessun Radix portal).
            Si chiude SOLO tramite i 3 pulsanti (nessun click sul backdrop, nessun Escape). */}
        {showSuccessModal && (
          <div
            className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4"
            data-testid="partner-offer-success-modal"
            aria-modal="true"
            role="dialog"
          >
            <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl border-2 border-emerald-300 p-6 sm:p-8" data-testid="partner-offer-success-modal-content">
              <div className="text-center">
                <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
                <h2 className="text-2xl font-black text-slate-800" data-testid="partner-offer-success-modal-title">
                  {submissionsCount > 1
                    ? `${submissionsCount} offerte inviate con successo!`
                    : "Offerta inviata con successo!"}
                </h2>
                {recentSubmission && (
                  <p className="text-sm text-slate-600 mt-2" data-testid="partner-offer-success-modal-meta">
                    Ultima: <strong>{recentSubmission.company || "—"}</strong>
                    {recentSubmission.frazionamento ? <> · frazionamento <strong>{recentSubmission.frazionamento}</strong></> : null}
                  </p>
                )}
              </div>

              <div className="mt-6 border-t border-slate-200 pt-5">
                <Label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">Frazionamento per la copia</Label>
                <Select value={dupFraz} onValueChange={setDupFraz}>
                  <SelectTrigger data-testid="po-dup-fraz-select" className="bg-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annuale">Annuale (1 rata)</SelectItem>
                    <SelectItem value="semestrale">Semestrale (2 rate)</SelectItem>
                    <SelectItem value="quadrimestrale">Quadrimestrale (3 rate)</SelectItem>
                    <SelectItem value="trimestrale">Trimestrale (4 rate)</SelectItem>
                    <SelectItem value="mensile">Mensile (12 rate)</SelectItem>
                  </SelectContent>
                </Select>

                <div className="grid grid-cols-1 gap-2 mt-4">
                  <Button
                    type="button"
                    onClick={duplicateLastOffer}
                    data-testid="po-duplicate-btn"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-5 text-base"
                  >
                    <Send className="h-4 w-4 mr-2" /> Duplica offerta
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={startNewOffer}
                    data-testid="po-new-offer-btn"
                    className="w-full font-semibold border-slate-400 py-5 text-base"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> Nuova offerta da zero
                  </Button>
                  <Button
                    type="button"
                    onClick={finishAndRedirect}
                    data-testid="po-finish"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-5 text-base"
                  >
                    Ho finito
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-3 text-center">
                  La modale si chiude solo tramite uno di questi pulsanti.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase text-slate-500 font-semibold">{k}</span>
      <span className="text-[13px] text-slate-800 font-medium truncate">{v || "—"}</span>
    </div>
  );
}

function fmtDate(v) {
  if (!v) return null;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
}

// 🆕 Feb 2026 — Default export: wrappa l'inner component con ErrorBoundary così la pagina
// non resta MAI bianca su Chrome (o qualunque altro browser) anche in caso di errore React.
export default function PartnerOfferSubmit() {
  return (
    <PartnerOfferErrorBoundary>
      <PartnerOfferSubmitInner />
    </PartnerOfferErrorBoundary>
  );
}
